import { getMp3InfoTagOffset } from "@/utils/audio/mp3/getMp3InfoTagOffset";
import { isMp3FrameBoundary } from "@/utils/audio/mp3/isMp3FrameBoundary";
import { parseMp3FrameHeader } from "@/utils/audio/mp3/parseMp3FrameHeader";
import { readId3v1Bytes } from "@/utils/getAudioLength/mp3/readId3v1Bytes";
import { readId3v2TotalBytes } from "@/utils/getAudioLength/mp3/readId3v2TotalBytes";

const FRAME_HEADER_BYTES = 4;
// MPEG-1 Layer II at 384 kbps and 32 kHz is the largest frame any table row allows (144 × 384000 / 32000), plus the padding slot
const MAX_FRAME_BYTES = Math.floor((144 * 384_000) / 32_000) + 1;
// How far past the current position the window must reach: the largest frame and the header that follows it
const LOOKAHEAD_BYTES = MAX_FRAME_BYTES + FRAME_HEADER_BYTES;
const READ_CHUNK_BYTES = 1024 * 1024;

// Measures an MP3 the way decoders do: by walking every frame header and adding up the audio each frame carries. Header summaries lie in ways this cannot: a Xing frame count survives truncation or concatenation, and music-metadata calls a file CBR from its first three frames and extrapolates from file size, which a VBR file with a low-bitrate silent intro and no Xing header turns into a length several times too long. Encoder padding that decoders trim via the LAME tag is counted here, a few hundredths of a second at real sample rates. Returns null when no frame can be found.
export const getMp3DurationFromFrames = async (
  audioFile: File
): Promise<number | null> => {
  const firstFrameOffset = await readId3v2TotalBytes(audioFile);
  const streamEnd = audioFile.size - (await readId3v1Bytes(audioFile));

  // The stream is read through a sliding window rather than one file-sized buffer, which a long upload would exhaust browser memory with. The window always starts at the current position and reaches far enough past it to hold the largest legal frame and the header after it, so a frame is never judged against a chunk edge
  let windowStart = firstFrameOffset;
  let windowBytes = new Uint8Array(0);
  const slideWindowTo = async (position: number): Promise<void> => {
    windowBytes = windowBytes.subarray(position - windowStart);
    windowStart = position;
    const requiredEnd = Math.min(position + LOOKAHEAD_BYTES, streamEnd);
    while (windowStart + windowBytes.length < requiredEnd) {
      const chunkStart = windowStart + windowBytes.length;
      const chunkEnd = Math.min(chunkStart + READ_CHUNK_BYTES, streamEnd);
      const chunk = new Uint8Array(
        await audioFile.slice(chunkStart, chunkEnd).arrayBuffer()
      );
      const joined = new Uint8Array(windowBytes.length + chunk.length);
      joined.set(windowBytes);
      joined.set(chunk, windowBytes.length);
      windowBytes = joined;
    }
  };

  let offset = firstFrameOffset;
  let durationS = 0;
  let frameCount = 0;
  while (offset + FRAME_HEADER_BYTES <= streamEnd) {
    await slideWindowTo(offset);
    const header = parseMp3FrameHeader(windowBytes, 0);
    // A header only counts when the frame it describes ends the stream or is followed by a frame boundary; anything else is a false sync inside junk bytes, skipped a byte at a time the way a decoder resyncs. A frame cut off by the end of the file is dropped, as decoders drop it.
    const isFrame =
      header !== null &&
      offset + header.frameBytes <= streamEnd &&
      (offset + header.frameBytes === streamEnd ||
        isMp3FrameBoundary(windowBytes, header.frameBytes));
    if (!isFrame) {
      offset += 1;
      continue;
    }

    // The Xing/Info frame is a silent placeholder that decoders skip rather than play
    const isPlaceholderFrame =
      frameCount === 0 && getMp3InfoTagOffset(windowBytes, 0, header) !== null;
    if (!isPlaceholderFrame) {
      durationS += header.samplesPerFrame / header.sampleRate;
      frameCount += 1;
    }
    offset += header.frameBytes;
  }

  return frameCount === 0 ? null : durationS;
};
